<script lang="ts">
  import pageUrl, { redirectTo } from "../../../PageUrl";
  import FormInput from "../../common/form/FormInputWithSurroundError.svelte";
  import Icon from "../../common/icons/Icon.svelte";
  import Link from "../../common/link/Link.svelte";
  import NavigatorLinkWrapper from "../../common/link/NavigatorLinkWrapper.svelte";
  import Button from "../../common/form/Button.svelte";
  import AuthFormHeader from "../common/AuthFormHeader.svelte";
  import AuthFormStyle from "../common/AuthFormStyle.svelte";
  import HorizontalDivider from "../common/HorizontalDivider.svelte";
  import { createForm } from "felte";
  import { validator } from "@felte/validator-yup";
  import * as yup from "yup";
  import { signIn, signInWithGoogle } from "../../../app/api";
  import type { APIError } from "../../../app/api";

  let email: string = "";
  let password: string = "";
  let submittedEmail: string = null;
  let submittedPassword: string = null;
  let isEmailFocus: boolean = false;
  let isPasswordFocus: boolean = false;
  let emailErrorMessage: string = "";
  let passwordErrorMessage: string = "";
  let serverError: string = "";
  let canSubmit: boolean = true;
  let isSignIn: boolean = false;
  let isPasswordVisible: boolean = false;

  const schema = yup.object({
    email: yup
      .string()
      .email("Invalid email address")
      .required("Please enter your email"),
    password: yup.string().required("Please enter your password"),
  });

  const { form, errors, validate } = createForm({
    extend: validator({ schema }),
    onSubmit: async () => {
      // Stop handle sign in if it's in progress
      if (isSignIn) return;
      // Validate sign in information again
      await validate();
      updateErrorMessage();
      // not allow to do log in if information is wrong
      if (!canSubmit) return;
      isSignIn = true;
      await signIn(email, password, {
        onSuccess: async () => {
          // redirectTo(pageUrl.USER_INFO);
        },
        onError: async (error: APIError) => {
          serverError = "";
          if (error.key === "email") {
            submittedEmail = email;
            emailErrorMessage = error.message;
          } else if (error.key === "password") {
            passwordErrorMessage = error.message;
            submittedPassword = password;
          } else {
            serverError = error.message;
          }
          updateErrorMessage();
        },
      });
      isSignIn = false;
    },
  });

  function updateErrorMessage() {
    let errs = $errors;
    if (!submittedEmail || submittedEmail !== email) {
      emailErrorMessage = errs.email ? errs.email[0] : "";
      submittedEmail = null;
    }
    if (!submittedPassword || submittedPassword !== password) {
      passwordErrorMessage = errs.password ? errs.password[0] : "";
      submittedPassword = null;
    }
    canSubmit = !Boolean(emailErrorMessage) && !Boolean(passwordErrorMessage);
  }

  function togglePasswordVisible() {
    isPasswordVisible = !isPasswordVisible;
  }

  async function handleSignInWithGoogle() {
    if (isSignIn) return;
    isSignIn = true;
    try {
      await signInWithGoogle();
    } catch (error) {}
    isSignIn = false;
  }

  async function handleFormSubmit() {
    await validate();
    updateErrorMessage();
  }

  // $: $errors, updateErrorMessage();
</script>

<div class="auth-form-container">
  <div class="form-content">
    <AuthFormHeader title="Sign In" />

    <form use:form method="POST" on:submit|capture={handleFormSubmit}>
      <div class="notify">
        <div><Icon icon="login-message-lock" size="32px" /></div>
        <div class="message">
          Datadocs is currently in development, and access is not yet public.
        </div>
      </div>

      <div class="input-field">
        <p>Email Address</p>
        <div class="input-container" class:focus={isEmailFocus}>
          <div class="prefix-icon">
            <Icon icon="input-email" width="20px" height="18px" />
          </div>
          <FormInput
            bind:value={email}
            bind:isFocus={isEmailFocus}
            errorMessage={emailErrorMessage}
            name="email"
            errorIndicatorIconSize="20px"
            type="email"
            spellcheck="false"
            placeholder="Enter Your Email"
            autocomplete="off"
            disabled={isSignIn}
          />
        </div>
      </div>

      <div class="input-field">
        <div class="flex flex-row items-center justify-between">
          <p>Password</p>
          <div class="forgot-password">
            <NavigatorLinkWrapper
              underlineOnHover={true}
              to={pageUrl.PASSWORD_RECOVERY}
              tabindex="-1"
            >
              Forgot Password ?
            </NavigatorLinkWrapper>
          </div>
        </div>
        <div class="input-container" class:focus={isPasswordFocus}>
          <div class="prefix-icon">
            <Icon icon="input-password" width="20px" height="20px" />
          </div>

          <FormInput
            bind:value={password}
            bind:isFocus={isPasswordFocus}
            errorMessage={passwordErrorMessage}
            name="password"
            showErrorIndicatorIcon={false}
            type={isPasswordVisible ? "text" : "password"}
            spellcheck="false"
            placeholder="Enter Your Password"
            autocomplete="off"
            disabled={isSignIn}
          />

          <div class="postfix-icon">
            <button
              type="button"
              on:click={togglePasswordVisible}
              class="w-5 h-5"
              tabindex="-1"
            >
              <Icon
                icon={isPasswordVisible
                  ? "password-visible"
                  : "password-invisible"}
                width="20px"
                height="20px"
              />
            </button>
          </div>
        </div>
      </div>

      <div class="submit-button mb-6">
        {#if serverError}
          <div class="pb-2 text-tertiary-error font-medium text-center">
            {serverError}
          </div>
        {/if}
        <div>
          <Button disabled={isSignIn}>Submit</Button>
        </div>
      </div>

      <HorizontalDivider>
        <div>
          <span class="font-medium">Don’t have an account?</span>
          <Link href={pageUrl.REGISTER} class="font-semibold">Create Now</Link>
        </div>
      </HorizontalDivider>

      <div
        class="google-account mt-5 flex flex-row items-center justify-center"
      >
        <Button
          disabled={isSignIn}
          color="secondary"
          type="button"
          on:click={handleSignInWithGoogle}
        >
          <Icon icon="google" size="20px" />
          <p>Continue with Google</p>
        </Button>
      </div>
    </form>
  </div>
</div>

<AuthFormStyle />

<style lang="postcss">
  .notify {
    @apply flex flex-row items-center rounded-md mb-6 bg-tertiary-orange bg-opacity-10;
    padding: 10px 20px;
    gap: 10px;
  }

  .message {
    @apply text-tertiary-orange;
    font-size: 13px;
  }

  .forgot-password {
    @apply font-medium text-right mb-1.5;
  }

  .forgot-password :global(a) {
    @apply text-dark-50;
    font-size: 13px;
    letter-spacing: 0px !important;
  }

  .submit-button {
    @apply mt-10 py-3 px-0;
    font-size: 13px;
  }

  .submit-button :global(button) {
    @apply w-full;
  }

  .google-account :global(button.secondary) {
    @apply w-full flex flex-row items-center justify-center py-3 px-0 gap-2.5 text-dark-200;
    font-size: 13px;
  }
</style>
